/**
 * Diff Viewer Component
 *
 * GitHub PR review style diff viewer with a collapsible top-level section,
 * collapsible file sections, a summary bar, and per-file addition/deletion badges.
 * File sections use CSS Grid row transitions to avoid nested max-height animation conflicts.
 */

import { Fragment, useCallback, useMemo, useState } from 'react';
import { ChevronRight, Eye, Code } from 'lucide-react';
import { parseDiff, isMarkdownFile, type ParsedDiff } from './DiffViewer.parsing';
import { MarkdownDiffPreview } from './DiffViewer.helpers';

interface DiffViewerProps {
  diff: string;
  files: string[];
  embedded?: boolean;
}

/** Visual bar showing the proportion of additions vs deletions for a file */
function ChangeBar({ additions, deletions }: { additions: number; deletions: number }) {
  const total = additions + deletions;
  if (total === 0) return null;

  const maxBlocks = 5;
  const addBlocks = Math.round((additions / total) * maxBlocks);
  const delBlocks = maxBlocks - addBlocks;

  return (
    <span className="inline-flex gap-px ml-2">
      {Array.from({ length: addBlocks }).map((_, i) => (
        <span key={`a-${i}`} className="w-1.5 h-1.5 rounded-sm bg-green-500" />
      ))}
      {Array.from({ length: delBlocks }).map((_, i) => (
        <span key={`d-${i}`} className="w-1.5 h-1.5 rounded-sm bg-red-500" />
      ))}
    </span>
  );
}

/** Renders the diff table for a single file */
function FileDiffContent({ file }: { file: ParsedDiff }) {
  return (
    <div className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 overflow-x-auto">
      <table className="w-full text-xs font-mono">
        <tbody>
          {file.chunks.map((chunk, chunkIdx) => (
            <Fragment key={chunkIdx}>
              {/* Chunk Header */}
              <tr>
                <td
                  colSpan={3}
                  className="px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                >
                  {chunk.header}
                </td>
              </tr>
              {/* Chunk Lines */}
              {chunk.lines.map((line, lineIdx) => {
                const bgColor =
                  line.type === 'add'
                    ? 'bg-green-50 dark:bg-green-900/20'
                    : line.type === 'delete'
                      ? 'bg-red-50 dark:bg-red-900/20'
                      : '';

                const textColor =
                  line.type === 'add'
                    ? 'text-green-800 dark:text-green-200'
                    : line.type === 'delete'
                      ? 'text-red-800 dark:text-red-200'
                      : 'text-gray-700 dark:text-gray-300';

                return (
                  <tr key={lineIdx} className={bgColor}>
                    {/* Old Line Number */}
                    <td className="px-2 py-0.5 text-right text-gray-400 dark:text-gray-500 select-none w-12">
                      {line.oldLineNum != null ? line.oldLineNum : ''}
                    </td>
                    {/* New Line Number */}
                    <td className="px-2 py-0.5 text-right text-gray-400 dark:text-gray-500 select-none w-12 border-l border-gray-200 dark:border-gray-700">
                      {line.newLineNum != null ? line.newLineNum : ''}
                    </td>
                    {/* Content */}
                    <td className={`px-3 py-0.5 whitespace-pre ${textColor}`}>
                      <span className="mr-2 select-none">
                        {line.type === 'add' ? '+' : line.type === 'delete' ? '-' : ' '}
                      </span>
                      {line.content}
                    </td>
                  </tr>
                );
              })}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DiffViewer({ diff, embedded = false }: DiffViewerProps) {
  const parsedDiff = useMemo(() => parseDiff(diff), [diff]);
  const [sectionExpanded, setSectionExpanded] = useState(false);
  const [expandedFiles, setExpandedFiles] = useState<Set<number>>(() => new Set());
  const [previewingFiles, setPreviewingFiles] = useState<Set<number>>(() => new Set());

  const toggleSection = useCallback(() => {
    setSectionExpanded((prev) => !prev);
  }, []);

  const toggleFile = useCallback((index: number) => {
    setExpandedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedFiles(new Set(parsedDiff.map((_, i) => i)));
  }, [parsedDiff]);

  const collapseAll = useCallback(() => {
    setExpandedFiles(new Set());
  }, []);

  const togglePreview = useCallback((index: number) => {
    setPreviewingFiles((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  if (!diff || parsedDiff.length === 0) {
    if (embedded) return null;
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <p className="text-gray-500 dark:text-gray-400 text-center">No changes to display</p>
      </div>
    );
  }

  const totalAdditions = parsedDiff.reduce((sum, file) => sum + file.additions, 0);
  const totalDeletions = parsedDiff.reduce((sum, file) => sum + file.deletions, 0);
  const allExpanded = expandedFiles.size === parsedDiff.length;

  return (
    <div
      className={embedded ? '' : 'bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden'}
    >
      {/* Summary Bar — clickable top-level toggle */}
      <button
        type="button"
        onClick={toggleSection}
        className="w-full px-4 py-3 border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-t-lg"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ChevronRight
              className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-300 ease-in-out ${
                sectionExpanded ? 'rotate-90' : ''
              }`}
            />
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Changes Preview
            </h3>
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-700 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300">
              {parsedDiff.length} {parsedDiff.length === 1 ? 'file' : 'files'}
            </span>
            <span className="text-xs font-medium text-green-600 dark:text-green-400">
              +{totalAdditions}
            </span>
            <span className="text-xs font-medium text-red-600 dark:text-red-400">
              -{totalDeletions}
            </span>
          </div>
        </div>
      </button>

      {/* File list — always visible so collapsed state still shows what changed */}
      {!sectionExpanded && (
        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700">
          {parsedDiff.map((file, fileIdx) => (
            <div key={fileIdx} className="flex items-center gap-2 py-0.5">
              <span className="text-xs font-mono text-gray-600 dark:text-gray-400 truncate">
                {file.fileName}
              </span>
              <div className="flex items-center gap-1.5 shrink-0 text-xs font-medium">
                {file.additions > 0 && (
                  <span className="text-green-600 dark:text-green-400">+{file.additions}</span>
                )}
                {file.deletions > 0 && (
                  <span className="text-red-600 dark:text-red-400">-{file.deletions}</span>
                )}
                <ChangeBar additions={file.additions} deletions={file.deletions} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Expanded: full diff viewer with per-file collapse */}
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: sectionExpanded ? '9999px' : '0' }}
      >
        {/* Expand/Collapse all bar */}
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (allExpanded) {
                collapseAll();
              } else {
                expandAll();
              }
            }}
            className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </button>
        </div>

        {/* File Sections */}
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {parsedDiff.map((file, fileIdx) => {
            const isExpanded = expandedFiles.has(fileIdx);
            const isMd = isMarkdownFile(file.fileName);
            const isPreviewing = previewingFiles.has(fileIdx);

            return (
              <div key={fileIdx}>
                {/* Clickable File Header */}
                <div className="flex items-center hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <button
                    type="button"
                    onClick={() => toggleFile(fileIdx)}
                    className="flex-1 flex items-center gap-2 px-4 py-2.5 text-left"
                  >
                    <ChevronRight
                      className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-300 ease-in-out ${
                        isExpanded ? 'rotate-90' : ''
                      }`}
                    />

                    <span className="flex-1 min-w-0 text-sm font-mono text-gray-900 dark:text-white truncate">
                      {file.fileName}
                    </span>

                    <div className="flex items-center gap-2 shrink-0 text-xs font-medium">
                      {file.additions > 0 && (
                        <span className="text-green-600 dark:text-green-400">
                          +{file.additions}
                        </span>
                      )}
                      {file.deletions > 0 && (
                        <span className="text-red-600 dark:text-red-400">-{file.deletions}</span>
                      )}
                      <ChangeBar additions={file.additions} deletions={file.deletions} />
                    </div>
                  </button>

                  {/* Diff / Preview toggle for markdown files */}
                  {isMd && isExpanded && (
                    <div className="flex items-center gap-0.5 pr-4 shrink-0">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isPreviewing) togglePreview(fileIdx);
                        }}
                        className={`p-1.5 rounded transition-colors ${
                          !isPreviewing
                            ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                        }`}
                        title="View diff"
                      >
                        <Code className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isPreviewing) togglePreview(fileIdx);
                        }}
                        className={`p-1.5 rounded transition-colors ${
                          isPreviewing
                            ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                        }`}
                        title="Preview markdown"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Animated file content — CSS Grid technique avoids nested max-height conflicts */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateRows: isExpanded ? '1fr' : '0fr',
                    transition: 'grid-template-rows 300ms ease-in-out',
                  }}
                >
                  <div style={{ overflow: 'hidden', minHeight: 0 }}>
                    {isMd && isPreviewing ? (
                      <MarkdownDiffPreview file={file} />
                    ) : (
                      <FileDiffContent file={file} />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
